import { isEmpty } from "lodash";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Dimmer, Loader } from "semantic-ui-react";
import { useDeleteMappingData } from "../../api/drawingTool";
import { useGetSalesUnit } from "../../api/product";
import { useGetSimulationDrawing } from "../../api/simulation";
import ConfirmModal from "../../components/confirmViewModal/ConfirmModal";
import { TitleBar } from "../../components/titleBar/Titlebar";
import { DISABLE_IS_ROOT_PRODUCT_BUTTON } from "../../config/constants";
import { MappingSourceType } from "../../config/enums/MappingSourceType.enum";
import { warningMessage } from "../../helpers/ErrorHandler";
import "./ProductSimulation.scss";
import ProductSimulationTable from "./component/SimulationTable";

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const ProductSimulation = () => {
  const [salesUnitId, setSalesUnitId] = useState("");
  const [productId, setProductId] = useState("");
  const [visibleDeleteModal, setVisibleDeleteModal] = useState(false);
  const [simulationData, setSimulationData] = useState<DrawingSimulationData[]>([]);
  const [deleteSimulationData, setDeleteSimulationData] = useState<DrawingSimulationData | null>(
    null
  );
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalSimulations, setTotalSimulations] = useState(0);
  const [currentDrawingId, setCurrentDrawingId] = useState(null);

  // Query params
  const query = useQuery();

  // History
  const navigate = useNavigate();

  const { data: productPayload, isLoading: isProductPayloadLoading } = useGetSalesUnit({
    sales_unit_id: salesUnitId,
    product_id: productId
  });

  const {
    data: drawingSimulationData,
    isFetching: isDrawingSimulationsLoading,
    refetch
  } = useGetSimulationDrawing({
    pageNumber,
    pageSize,
    ownerId: productId,
    sourceId: salesUnitId
  });

  const { mutate, isLoading: isDeleteMappingLoading } = useDeleteMappingData(
    MappingSourceType.simulation
  );

  const deleteSimulationMapping = () => {
    mutate(deleteSimulationData?._id, {
      onSettled: () => {
        setVisibleDeleteModal(false);
        setDeleteSimulationData(null);
        refetch();
      }
    });
  };

  useEffect(() => {
    const salesUnitId = query.get("salesUnitId");
    const productId = query.get("productId");
    if (salesUnitId && productId) {
      setSalesUnitId(salesUnitId);
      setProductId(productId);
    }
  }, []);

  useEffect(() => {
    if (drawingSimulationData) {
      setSimulationData(drawingSimulationData.result || []);
      setPageNumber(drawingSimulationData.page);
      setPageSize(drawingSimulationData.pageSize);
      setTotalSimulations(drawingSimulationData.total);
    }
  }, [drawingSimulationData]);

  const navigateToSimulateDrawing = (itemId: string, navigationId?: number) => {
    switch (navigationId) {
      case 0:
        return navigate(
          `/mapping/simulate-mapping/${productId}/${salesUnitId}?simulateDrawingId=${itemId}`
        );
      default:
        return !isEmpty(currentDrawingId)
          ? navigate(
              `/simulations/product-simulation-net-zero?salesUnitId=${salesUnitId}&productId=${productId}&simulationDrawingId=${currentDrawingId}`
            )
          : warningMessage("Please select net zero simulation");
    }
  };

  const isLoading = Boolean(isProductPayloadLoading);

  if (isLoading || isDeleteMappingLoading) {
    return (
      <Dimmer active>
        <Loader content="Loading" />
      </Dimmer>
    );
  }

  return (
    <div>
      <TitleBar
        enableTitle={true}
        titleName={
          (productPayload &&
            `${productPayload?.product_name} - ${productPayload?.sales_unit_name}`) ||
          ""
        }
        customButtonData={[
          {
            text: "Go to Simulation",
            skip: false
          },
          {
            text: "Net Zero Journey - Beta",
            skip: !DISABLE_IS_ROOT_PRODUCT_BUTTON ? productPayload?.is_average_product : false
          }
        ]}
        enableCustomButton
        buttonSelectView={false}
        customPageButtonContainer="product-title-container"
        customPageTitle="product-simulation-title"
        customButtonMain="title-button"
        customButtonAction={(index: any) => {
          navigateToSimulateDrawing("", index);
        }}
      />
      <ProductSimulationTable
        data={simulationData}
        onEdit={item => {
          navigateToSimulateDrawing(item._id, 0);
        }}
        onDelete={item => {
          setDeleteSimulationData(item);
          setVisibleDeleteModal(true);
        }}
        setPageNumber={setPageNumber}
        setPageSize={setPageSize}
        pageNumber={pageNumber}
        pageSize={pageSize}
        totalSimulations={totalSimulations}
        isGetProductSimulationsLoading={isDrawingSimulationsLoading}
        currentDrawingId={(id: any) => setCurrentDrawingId(id)}
      />
      <ConfirmModal
        viewModal={visibleDeleteModal}
        closeModal={() => setVisibleDeleteModal(false)}
        cancel={() => {
          setVisibleDeleteModal(false);
          setDeleteSimulationData(null);
        }}
        approve={() => deleteSimulationMapping()}
        title="Delete Simulation Data"
        subTitle="Are you sure you want to delete this simulation data?"
      />
    </div>
  );
};

export default ProductSimulation;
